import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  ACCOUNT_TYPES,
  type AccountType,
  type CommitImportResult,
  type ImportMappings,
  type ImportPreview,
  type ValidationMessage,
} from '@accounting/shared';
import {
  useCommitImport,
  useDeleteImport,
  useImportSessions,
  useResumeImport,
  useRollbackImport,
  useUploadImport,
} from '../../hooks/useImports';
import { useAccounts } from '../../hooks/useAccounts';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { FileDropZone } from '../../components/ui/FileDropZone';
import { ApiError } from '../../lib/api';
import { formatAccountingDate } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/import/')({
  component: ImportPage,
});

function errMsg(err: unknown): string | undefined {
  if (err instanceof ApiError) {
    const body = err.body as
      | { error?: string; fields?: Array<{ instancePath?: string; message?: string }> }
      | null;
    let msg = body?.error ?? 'Request failed';
    // AJV schema rejections come back as { error: 'Validation failed', fields: [...] };
    // surface the field detail so it isn't just a bare "Validation failed".
    if (Array.isArray(body?.fields) && body!.fields.length) {
      const detail = body!.fields
        .map((f) => `${(f.instancePath || '').replace(/^\//, '').replace(/\//g, ' › ') || 'field'} ${f.message ?? ''}`.trim())
        .join('; ');
      if (detail) msg += `: ${detail}`;
    }
    return msg;
  }
  return err instanceof Error ? err.message : undefined;
}

/**
 * Clean a QBO account name for use as a NEW account name. QBO appends " (deleted)" to
 * accounts that were deleted in QuickBooks; don't carry that suffix into a fresh account.
 */
function cleanAccountName(qboName: string): string {
  return qboName.replace(/\s*\(deleted\)\s*$/i, '').trim() || qboName;
}

/**
 * Friendly message for a browser file-READ failure (before any upload). Chromium throws
 * DOMException "NotFoundError" ("…could not be found at the time an operation was
 * processed") when the picked file can't be read — typically an online-only OneDrive
 * file, one still open/locked in Excel, or one moved after it was selected.
 */
function fileReadErrorMessage(e: unknown): string {
  const raw = e instanceof Error ? e.message : '';
  const isNotFound =
    (typeof DOMException !== 'undefined' && e instanceof DOMException && e.name === 'NotFoundError') ||
    /could not be found at the time an operation was processed/i.test(raw);
  if (isNotFound) {
    return (
      'Could not read that file. It may be an online-only OneDrive file, still open in ' +
      'Excel, or it was moved after you selected it. In File Explorer right-click it → ' +
      '"Always keep on this device" (or re-save it to your Downloads folder), close Excel, ' +
      'then upload again.'
    );
  }
  return raw || 'Could not read that file.';
}

function ImportPage() {
  const companyId = useActiveCompanyId();
  const { data: sessions = [] } = useImportSessions(companyId);
  const upload = useUploadImport(companyId);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<CommitImportResult | null>(null);
  const [convertError, setConvertError] = useState<string | null>(null);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Import from QuickBooks</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  async function onFile(file: File) {
    setConvertError(null);
    const isExcel =
      /\.xlsx?$/i.test(file.name) ||
      file.type.includes('spreadsheetml') ||
      file.type.includes('ms-excel');
    try {
      let fileName = file.name;
      let content: string;
      if (isExcel) {
        // Convert the first sheet to CSV in the browser (SheetJS, lazy-loaded), then
        // run it through the normal CSV import pipeline — no backend changes needed.
        const XLSX = await import('xlsx');
        const wb = XLSX.read(new Uint8Array(await file.arrayBuffer()), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]!];
        if (!ws) throw new Error('That Excel file has no sheets.');
        content = XLSX.utils.sheet_to_csv(ws);
        fileName = file.name.replace(/\.xlsx?$/i, '.csv');
      } else {
        content = await file.text();
      }
      upload.mutate(
        { fileName, content },
        {
          onSuccess: (p) => {
            setPreview(p);
            setResult(null);
          },
        },
      );
    } catch (e) {
      setConvertError(fileReadErrorMessage(e));
    }
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '0 0 4px' }}>Import from QuickBooks</h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Export a Trial Balance, Transaction Detail by Account, General Ledger, Chart of Accounts, or
        Vendor/Customer list from QBO (CSV or Excel), then upload it here. The General Ledger is read the same
        way as Transaction Detail — beginning-balance and subtotal rows are ignored, and both the
        single-Amount and separate Debit/Credit column layouts are supported.
      </p>

      {!preview && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>
            Upload a CSV or Excel file.{' '}
            <span className="muted" style={{ fontWeight: 400 }}>
              Excel (.xlsx) is converted to CSV automatically; plain CSVs should be UTF-8.
            </span>
          </p>
          <FileDropZone
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            busy={upload.isPending}
            hint="QuickBooks export (.csv or .xlsx)"
            onFiles={(files) => void onFile(files[0]!)}
          />
          {convertError && <div className="field-error" style={{ marginTop: 8 }}>{convertError}</div>}
          {upload.error && <div className="field-error" style={{ marginTop: 8 }}>{errMsg(upload.error)}</div>}
        </div>
      )}

      {preview && !result?.committed && (
        <ImportWizard
          companyId={companyId}
          preview={preview}
          onDone={(r) => setResult(r)}
          onCancel={() => {
            setPreview(null);
          }}
        />
      )}

      {result?.committed && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <span className="tag ok">imported</span>
          <p style={{ marginBottom: 0 }}>
            Created {result.session.summary?.entriesCreated ?? 0} journal entr
            {result.session.summary?.entriesCreated === 1 ? 'y' : 'ies'},{' '}
            {result.session.summary?.accountsCreated ?? 0} account
            {result.session.summary?.accountsCreated === 1 ? '' : 's'},{' '}
            {result.session.summary?.contactsCreated ?? 0} contact
            {result.session.summary?.contactsCreated === 1 ? '' : 's'}.
          </p>
          <ValidationList messages={result.validation.filter((v) => v.severity === 'warning')} />
          <button
            className="btn-secondary"
            style={{ marginTop: 12 }}
            onClick={() => {
              setPreview(null);
              setResult(null);
            }}
          >
            Import another file
          </button>
        </div>
      )}

      <PastImports
        companyId={companyId}
        sessions={sessions}
        onResume={(p) => {
          setPreview(p);
          setResult(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    </div>
  );
}

function ImportWizard({
  companyId,
  preview,
  onDone,
  onCancel,
}: {
  companyId: string;
  preview: ImportPreview;
  onDone: (r: CommitImportResult) => void;
  onCancel: () => void;
}) {
  const commit = useCommitImport(companyId);
  const { data: accounts = [] } = useAccounts(companyId);
  const postable = accounts.filter((a) => a.isActive && a.allowDirectPosting);

  const needsMapping = preview.importType === 'trial_balance' || preview.importType === 'transactions';
  const [mappings, setMappings] = useState<ImportMappings>(() => {
    const m: ImportMappings = {};
    for (const a of preview.accounts) {
      m[a.qboAccountName] = a.suggestion.action === 'create'
        ? { action: 'create', newAccount: { code: '', name: cleanAccountName(a.qboAccountName), accountType: 'expense' } }
        : a.suggestion;
    }
    return m;
  });
  const [errors, setErrors] = useState<ValidationMessage[]>([]);
  // Transactions only: import entries whose number already exists in the books.
  const isTransactions = preview.importType === 'transactions';
  const [allowDuplicates, setAllowDuplicates] = useState(false);

  function update(name: string, patch: Partial<ImportMappings[string]>) {
    setMappings((prev) => ({ ...prev, [name]: { ...prev[name]!, ...patch } }));
  }
  function updateNew(name: string, patch: Partial<NonNullable<ImportMappings[string]['newAccount']>>) {
    setMappings((prev) => {
      const cur = prev[name]!;
      const newAccount = {
        code: cur.newAccount?.code ?? '',
        name: cur.newAccount?.name ?? cleanAccountName(name),
        accountType: cur.newAccount?.accountType ?? ('expense' as AccountType),
        accountSubtype: cur.newAccount?.accountSubtype,
        ...patch,
      };
      return { ...prev, [name]: { ...cur, newAccount } };
    });
  }

  function doCommit() {
    // Validate the mapping choices first, with a specific reason per problem, so the
    // user never gets a bare "Validation failed" from the server schema check.
    if (needsMapping) {
      const problems: ValidationMessage[] = [];
      for (const [name, m] of Object.entries(mappings)) {
        if (m.action === 'map' && !m.accountId) {
          problems.push({ severity: 'error', message: `"${name}": choose an account to map to, or set it to Create new / Skip.` });
        }
        // Code is optional — the server auto-assigns one when it's left blank.
        if (m.action === 'create' && !m.newAccount?.name?.trim()) {
          problems.push({ severity: 'error', message: `"${name}": enter a name for the new account.` });
        }
      }
      if (problems.length > 0) {
        setErrors(problems);
        return;
      }
    }
    setErrors([]);
    commit.mutate(
      {
        id: preview.session.id,
        input: {
          mappings: needsMapping ? mappings : undefined,
          ...(isTransactions ? { allowDuplicates } : {}),
        },
      },
      {
        onSuccess: (r) => {
          if (r.committed) onDone(r);
          else {
            const errs = r.validation.filter((v) => v.severity === 'error');
            // Never leave the user with no reason — fall back to any message we have.
            setErrors(errs.length ? errs : [{ severity: 'error', message: 'Import failed validation. See the messages above, or check the file and mappings.' }]);
          }
        },
      },
    );
  }

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 18, margin: 0 }}>
          {preview.importType.replace(/_/g, ' ')} · {preview.session.fileName}
        </h2>
        <span className="muted" style={{ fontSize: 13 }}>{preview.session.rowCount} rows</span>
      </div>

      <ValidationList messages={preview.validation} />

      {needsMapping && (
        <table className="table" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>QuickBooks account</th>
              <th>Action</th>
              <th>Maps to / new account</th>
            </tr>
          </thead>
          <tbody>
            {preview.accounts.map((a) => {
              const m = mappings[a.qboAccountName]!;
              return (
                <tr key={a.qboAccountName}>
                  <td>{a.qboAccountName}</td>
                  <td>
                    <select
                      value={m.action}
                      onChange={(e) => {
                        const action = e.target.value as 'map' | 'create' | 'skip';
                        // Seed the new-account fields (with a "(deleted)"-stripped name)
                        // when switching to Create, so the name box is prefilled.
                        update(
                          a.qboAccountName,
                          action === 'create'
                            ? {
                                action,
                                newAccount: m.newAccount ?? {
                                  code: '',
                                  name: cleanAccountName(a.qboAccountName),
                                  accountType: 'expense',
                                },
                              }
                            : { action },
                        );
                      }}
                    >
                      <option value="map">Map to existing</option>
                      <option value="create">Create new</option>
                      <option value="skip">Skip</option>
                    </select>
                  </td>
                  <td>
                    {m.action === 'map' && (
                      <select
                        value={m.accountId ?? ''}
                        onChange={(e) => update(a.qboAccountName, { accountId: e.target.value })}
                      >
                        <option value="">— select —</option>
                        {postable.map((acct) => (
                          <option key={acct.id} value={acct.id}>{acct.code} — {acct.name}</option>
                        ))}
                      </select>
                    )}
                    {m.action === 'create' && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <input
                          placeholder="Code (optional)"
                          value={m.newAccount?.code ?? ''}
                          onChange={(e) => updateNew(a.qboAccountName, { code: e.target.value })}
                          style={{ width: 110 }}
                        />
                        <input
                          placeholder="Name"
                          value={m.newAccount?.name ?? ''}
                          onChange={(e) => updateNew(a.qboAccountName, { name: e.target.value })}
                          style={{ width: 160 }}
                        />
                        <select
                          value={m.newAccount?.accountType ?? 'expense'}
                          onChange={(e) => updateNew(a.qboAccountName, { accountType: e.target.value as AccountType })}
                        >
                          {ACCOUNT_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {m.action === 'skip' && <span className="muted">— not imported —</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!needsMapping && (
        <p className="muted" style={{ fontSize: 13 }}>
          {preview.importType === 'chart_of_accounts'
            ? 'New accounts will be created from this chart of accounts.'
            : 'New contacts will be created from this list.'}
        </p>
      )}

      {errors.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <strong style={{ fontSize: 13, color: 'var(--bad, #C23B22)' }}>
            Import blocked — fix the following:
          </strong>
          <ValidationList messages={errors} />
        </div>
      )}
      {commit.error && <div className="field-error" style={{ marginTop: 8 }}>{errMsg(commit.error)}</div>}

      {isTransactions && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 16, fontSize: 13, maxWidth: 620 }}>
          <input
            type="checkbox"
            checked={allowDuplicates}
            onChange={(e) => setAllowDuplicates(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            <strong>Import duplicate entry numbers.</strong>{' '}
            <span className="muted">
              By default an entry whose number already exists in the books is skipped (so re-running an
              import doesn’t double-post). Turn this on for older books where the same number was reused
              across genuinely different entries — every entry in the file will be imported.
            </span>
          </span>
        </label>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="btn-primary" onClick={doCommit} disabled={commit.isPending}>
          {commit.isPending ? 'Importing…' : 'Commit import'}
        </button>
        <button className="btn-secondary" onClick={onCancel} disabled={commit.isPending}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ValidationList({ messages }: { messages: ValidationMessage[] }) {
  if (messages.length === 0) return null;
  return (
    <ul style={{ margin: '12px 0', paddingLeft: 18 }}>
      {messages.map((m, i) => (
        <li
          key={i}
          style={{ fontSize: 13, color: m.severity === 'error' ? 'var(--bad, #C23B22)' : 'var(--text-muted)' }}
        >
          <strong>{m.severity === 'error' ? 'Error: ' : 'Warning: '}</strong>
          {m.message}
          {m.row ? ` (row ${m.row})` : ''}
        </li>
      ))}
    </ul>
  );
}

function PastImports({
  companyId,
  sessions,
  onResume,
}: {
  companyId: string;
  sessions: import('@accounting/shared').ImportSession[];
  onResume: (preview: ImportPreview) => void;
}) {
  const rollback = useRollbackImport(companyId);
  const resume = useResumeImport(companyId);
  const del = useDeleteImport(companyId);
  if (sessions.length === 0) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <h2 style={{ fontSize: 16 }}>Import history</h2>
      <table className="table">
        <thead>
          <tr>
            <th>File</th>
            <th>Type</th>
            <th>Status</th>
            <th>When</th>
            <th style={{ width: 1 }}></th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s.id}>
              <td>{s.fileName}</td>
              <td>{s.importType.replace(/_/g, ' ')}</td>
              <td>
                {s.status === 'committed' && <span className="tag ok">committed</span>}
                {s.status === 'rolled_back' && <span className="tag bad">rolled back</span>}
                {s.status === 'pending' && <span className="tag warn">pending</span>}
                {(s.status === 'failed' || s.status === 'validated') && <span className="tag">{s.status}</span>}
              </td>
              <td className="muted">
                {s.committedAt ? formatAccountingDate(s.committedAt.slice(0, 10)) : formatAccountingDate(s.createdAt.slice(0, 10))}
              </td>
              <td>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  {['pending', 'validated', 'failed'].includes(s.status) && (
                    <button
                      className="btn-secondary"
                      disabled={resume.isPending}
                      onClick={() => resume.mutate(s.id, { onSuccess: (p) => onResume(p) })}
                    >
                      Resume
                    </button>
                  )}
                  {s.rollbackAvailable && (
                    <button
                      className="btn-danger"
                      disabled={rollback.isPending}
                      onClick={() => {
                        if (
                          confirm(
                            'Roll back this import?\n\nThis PERMANENTLY DELETES the journal entries AND the accounts this import created — they are removed entirely, not just deactivated. This cannot be undone.',
                          )
                        ) {
                          rollback.mutate(s.id);
                        }
                      }}
                    >
                      Roll back
                    </button>
                  )}
                  {s.status !== 'committed' && (
                    <button
                      className="btn-secondary"
                      disabled={del.isPending}
                      onClick={() => {
                        if (confirm('Discard this import session? It has not posted anything.')) {
                          del.mutate(s.id);
                        }
                      }}
                    >
                      Discard
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {(rollback.error || resume.error || del.error) && (
        <div className="field-error">
          {errMsg(rollback.error) ?? errMsg(resume.error) ?? errMsg(del.error)}
        </div>
      )}
    </div>
  );
}
